import { DeleteOutlined, DownOutlined } from "@ant-design/icons";
import { Button, Dropdown, Form, Table } from "antd";
import { ColumnsType } from "antd/es/table";
import * as React from "react";
import {
  extensionEventLabels,
  extensionToBound,
  removeBindingForEvent,
  setBindingForEvent,
  ZExtensionEventType,
  ZExtensionFormBinding,
} from "src/business/extensionBindings";
import { ZExtension } from "src/business/extensions/ZExtension";
import {
  getStepEventOptions,
  sortStepBindings,
} from "src/business/workflow/workflowStepExtensions";
import { BlankLink } from "src/components/BlankLink/BlankLink";
import { RenderButtonAction } from "src/components/MdmTable";
import { ExtensionSelectModal } from "src/pages/management/objects/common/EventsAndExtensions";
import { makeUrl } from "src/routes/makeUrl";
import { PageUrl } from "src/routes/PageUrl";

import * as styles from "./WorkflowStepExtensions.module.less";

type PropsWorkflowStepExtensions = {
  value?: ZExtensionFormBinding[] | null;
  onChange?(value: ZExtensionFormBinding[]): void;
};

/**
 * Блок «Расширение» в настройках шага: по одному расширению на событие
 */
export const WorkflowStepExtensions: React.FC<PropsWorkflowStepExtensions> = ({
  value,
  onChange,
}) => {
  const bindings = value ?? [];
  // Событие, для которого открыто окно выбора расширения
  const [event, setEvent] = React.useState<ZExtensionEventType>();
  const options = getStepEventOptions(bindings);
  const allTaken = options.every(({ disabled }) => disabled);
  const onSelect = (extension: ZExtension) => {
    if (event) {
      onChange?.(
        setBindingForEvent(bindings, event, extensionToBound(extension)),
      );
    }
    setEvent(undefined);
  };
  const columns: ColumnsType<ZExtensionFormBinding> = [
    {
      key: "displayId",
      title: "Display ID",
      width: 110,
      render: (_, { extension }) => extension.displayId,
    },
    {
      key: "name",
      title: "Название",
      render: (_, { extension: { id, name } }) => (
        <BlankLink
          href={makeUrl(PageUrl.managementExtensionCard, { id })}
          title="Открыть карточку расширения в новой вкладке"
        >
          {name}
        </BlankLink>
      ),
    },
    {
      key: "event",
      title: "Событие",
      render: (_, binding) => extensionEventLabels[binding.event],
    },
    {
      key: "delete",
      width: 48,
      render: (_, binding) => (
        <RenderButtonAction
          icon={<DeleteOutlined />}
          title={`Удалить связь с расширением "${binding.extension.name}"`}
          onClick={() =>
            onChange?.(removeBindingForEvent(bindings, binding.event))
          }
        />
      ),
    },
  ];
  return (
    <div className={styles.frame}>
      <Form.Item
        label="Расширение"
        tooltip="Код, расширяющий стандартные возможности системы"
        className={styles.formItem}
      />
      <div className={styles.add}>
        <Dropdown
          disabled={allTaken}
          trigger={["click"]}
          menu={{
            items: options.map((option) => ({
              key: option.event,
              label: option.label,
              disabled: option.disabled,
              onClick: () => setEvent(option.event),
            })),
          }}
        >
          <Button disabled={allTaken}>
            Добавить к событию
            <DownOutlined />
          </Button>
        </Dropdown>
      </div>
      <div className={styles.table}>
        <Table
          rowKey="event"
          dataSource={sortStepBindings(bindings)}
          columns={columns}
          pagination={false}
        />
      </div>
      {event && (
        <ExtensionSelectModal
          scope="WORKFLOW_STEP"
          close={() => setEvent(undefined)}
          onSelect={onSelect}
        />
      )}
    </div>
  );
};
